import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FlightRawEventName,
  FlightSearchSelectors,
  getDestination,
  isInitializingValues,
  useChooseFare,
  useEmitCoreFlightRawEvent,
  useSelectDepartureFlight,
  getFilteredAndSortedDepartureFlightList,
  getSortOption,
  setSortOption,
  FlightShopSelectors,
} from "@b2bportal/core-flights";
import {
  type AppDispatch,
  CallState,
  type SortOptionEnum,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { TripCategory } from "@overrides/types";
import { AccountSelectors } from "@/modules/account";
import {
  getIsSearchedRouteUnavailableForGridPurchase,
  type PricingMode,
  togglePricingMode,
} from "@/store/flights/shop/store";

export const useDeparturePageContainer = ({
  goToNextStep,
  onErrorReload,
  pricingMode,
}: {
  goToNextStep: () => void;
  onErrorReload: () => void;
  pricingMode: PricingMode;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const sortOption = useSelector(getSortOption);
  const handleSortChange = useCallback(
    (value: string) => {
      dispatch(setSortOption(value as SortOptionEnum));
    },
    [dispatch]
  );
  const { t } = useI18nContext();

  const routeNotAllowedForFixedPointsPricing = useSelector(
    getIsSearchedRouteUnavailableForGridPurchase
  );

  const [isAllCollapsed, setIsAllCollapsed] = useState<boolean>(false);
  const isError = useSelector(FlightShopSelectors.getFlightsErrorState);
  const isRewardsLoading = useSelector(AccountSelectors.isRewardsLoading);
  const flightsCallState = useSelector(FlightShopSelectors.getFlightsCallState);
  const isFlightsLoading =
    flightsCallState === CallState.InProcess ||
    flightsCallState === CallState.NotCalled;

  const isInitializing = useSelector(isInitializingValues);
  const flightList = useSelector(getFilteredAndSortedDepartureFlightList);

  const destination = useSelector(getDestination);
  const tripCategory = useSelector(FlightSearchSelectors.getTripCategory);

  const handleFareSelect = useSelectDepartureFlight();

  const handleChooseFare = useChooseFare(goToNextStep);

  const text = useMemo(() => {
    const subtitle = () => {
      switch (tripCategory) {
        case TripCategory.ONE_WAY:
          return t("core-flights.shopPage.subtitle.oneWay");
        case TripCategory.ROUND_TRIP:
          return t("core-flights.shopPage.subtitle.roundTrip");
        case TripCategory.MULTI_CITY:
          return t("core-flights.shopPage.subtitle.multiCity");
      }
    };

    return {
      pageTitle: t("core-flights.departurePage.title.short"),
      subtitle: subtitle(),
    };
  }, [tripCategory, t]);

  const emitViewedFlightList = useEmitCoreFlightRawEvent(
    FlightRawEventName.ViewedFlightList
  );

  const setPricingMode = useCallback(
    (mode: PricingMode) => {
      setIsAllCollapsed(true);
      dispatch(togglePricingMode({ pricingMode: mode, tripCategory }));
      emitViewedFlightList({});
    },
    [dispatch, setIsAllCollapsed, tripCategory, emitViewedFlightList]
  );

  return useMemo(() => {
    return {
      context: {
        isLoading: isRewardsLoading || isFlightsLoading,
        isError,
        isInitializing,
        flightList,
        destination,
        tripCategory,
        handleFareSelect,
        sortOption,
        isFixedPriceRoute: !routeNotAllowedForFixedPointsPricing,
        pricingMode,
        isAllCollapsed,
      },
      handlers: {
        handleFareSelect,
        handleSortChange,
        handleChooseFare,
        onErrorReload,
        setPricingMode,
        setIsAllCollapsed,
      },
      text,
    };
  }, [
    isRewardsLoading,
    isFlightsLoading,
    isError,
    isInitializing,
    flightList,
    destination,
    tripCategory,
    handleFareSelect,
    sortOption,
    routeNotAllowedForFixedPointsPricing,
    pricingMode,
    isAllCollapsed,
    setIsAllCollapsed,
    handleSortChange,
    handleChooseFare,
    onErrorReload,
    setPricingMode,
    text,
  ]);
};
